import axios from 'axios';

/**
 * Custom Dashboards API client. Talks to ribeye-api's owner-scoped
 * `/custom-dashboards` CRUD endpoints. The OAT bearer is attached by the global
 * axios interceptor (AxiosProvider); `VITE_BASE_API_URL` already includes the
 * `/api/v1` prefix, so paths are relative.
 */

export type DashboardWidget = Record<string, unknown>;

export interface CustomDashboard {
  id: number;
  name: string;
  description?: string;
  layout: DashboardWidget[];
  /** Dashboard-level settings (cascading filters). Null on older dashboards. */
  config?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

const PATH = '/custom-dashboards';

export const listDashboards = async (): Promise<CustomDashboard[]> => {
  const response = await axios.get(PATH);
  return (response.data?.data ?? []) as CustomDashboard[];
};

export const getDashboard = async (id: number): Promise<CustomDashboard> => {
  const response = await axios.get(`${PATH}/${id}`);
  return response.data.data as CustomDashboard;
};

export const createDashboard = async (
  name: string,
  description?: string,
  layout?: DashboardWidget[],
  config?: Record<string, unknown> | null,
): Promise<CustomDashboard> => {
  const response = await axios.post(PATH, {
    name,
    description,
    layout: layout ?? [],
    ...(config ? { config } : {}),
  });
  return response.data.data as CustomDashboard;
};

export const updateDashboard = async (
  id: number,
  patch: { name?: string; layout?: DashboardWidget[]; config?: Record<string, unknown> },
): Promise<CustomDashboard> => {
  const response = await axios.put(`${PATH}/${id}`, patch);
  return response.data.data as CustomDashboard;
};

export const deleteDashboard = async (id: number): Promise<void> => {
  await axios.delete(`${PATH}/${id}`);
};

// ── tenant filter defaults ───────────────────────────────────────────────────
// The middle layer of the slice F resolver (dashboard config → tenant → built-in).
// Always scoped server-side to the caller's own whitelabel, so there is no id.

/** `null` means the tenant has no opinion; `[]` means an admin disabled every filter. */
export const getTenantFilterDefaults = async (): Promise<string[] | null> => {
  const response = await axios.get(`${PATH}/filter-defaults`);
  return (response.data?.data?.enabled_filter_keys ?? null) as string[] | null;
};

export const setTenantFilterDefaults = async (keys: string[]): Promise<string[]> => {
  const response = await axios.put(`${PATH}/filter-defaults`, { enabled_filter_keys: keys });
  return (response.data?.data?.enabled_filter_keys ?? []) as string[];
};

/** Clear the tenant opinion so dashboards fall back to the built-in default. */
export const clearTenantFilterDefaults = async (): Promise<void> => {
  await axios.delete(`${PATH}/filter-defaults`);
};
